import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Booking } from '../../types';

export function Analytics() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    sales: Array<{
      month: string;
      sales: number;
      orders: number;
    }>;
    categories: Array<{
      name: string;
      value: number;
    }>;
  }>({ sales: [], categories: [] });
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<Booking | null>(null);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B'];

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get bookings from localStorage
      const storedBookings = localStorage.getItem('bookings');
      const bookings: Booking[] = storedBookings ? JSON.parse(storedBookings) : [];

      // Filter completed orders (status is 'Done')
      const completedOrders = bookings.filter(booking => 
        booking.status === 'Done' && 
        booking.statusHistory?.some(h => h.status === 'Done')
      );

      // Calculate monthly sales data
      const monthlySalesMap = new Map();
      const now = new Date();
      
      // Initialize last 6 months
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = date.toLocaleString('default', { month: 'short' });
        monthlySalesMap.set(monthKey, { sales: 0, orders: 0 });
      }

      // Populate with actual data
      completedOrders.forEach(order => {
        const completionDate = new Date(
          order.statusHistory.find(h => h.status === 'Done')?.timestamp || ''
        );
        const monthKey = completionDate.toLocaleString('default', { month: 'short' });
        
        if (monthlySalesMap.has(monthKey)) {
          const monthData = monthlySalesMap.get(monthKey);
          const price = parseFloat(order.product.price.toString()) || 0;
          monthData.sales += price;
          monthData.orders += 1;
        }
      });

      // Convert map to array
      const salesData = Array.from(monthlySalesMap.entries()).map(([month, data]) => ({
        month,
        sales: parseFloat(data.sales.toFixed(2)),
        orders: data.orders
      }));

      // Calculate category distribution
      const categoryMap = new Map();
      completedOrders.forEach(order => {
        const category = order.product.type || 'Other';
        categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
      });

      const categoryData = Array.from(categoryMap.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value
        }));

      setData({
        sales: salesData,
        categories: categoryData
      });
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 2000);

    return () => clearInterval(interval);
  }, [refreshKey]);

  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg flex items-start">
        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2" />
        <div>
          <h3 className="text-red-800 font-medium">Error Loading Analytics</h3>
          <p className="text-red-700 mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-3 text-red-600 hover:text-red-800 font-medium flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const metrics = {
    salesByMonth: data.sales.map(item => ({
      name: item.month,
      sales: item.sales,
      orders: item.orders
    })),
    categories: data.categories,
    refundMetrics: {
      total: 0,
      amount: 0,
      recentRefunds: []
    }
  };

  const stats = [
    {
      name: 'Total Revenue',
      value: `$${data.sales.reduce((sum, month) => sum + month.sales, 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`,
      change: '+12.3%',
      trend: 'up',
      icon: RefreshCw
    },
    {
      name: 'Total Orders',
      value: data.sales.reduce((sum, month) => sum + month.orders, 0).toLocaleString(),
      change: '+5.4%',
      trend: 'up',
      icon: RefreshCw
    },
    {
      name: 'Average Order Value',
      value: `$${(data.sales.reduce((sum, month) => sum + month.sales, 0) / 
        data.sales.reduce((sum, month) => sum + month.orders, 0)).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`,
      change: '-2.1%',
      trend: 'down',
      icon: RefreshCw
    },
    {
      name: 'Monthly Growth',
      value: '+8.2%',
      change: '+1.2%',
      trend: 'up',
      icon: RefreshCw
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid - Updated for better mobile layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`p-2 sm:p-3 rounded-full ${
                stat.trend === 'up' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${
                  stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`} />
              </div>
            </div>
            <div className="mt-2">
              <span className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
              <span className="text-sm text-gray-600"> from last month</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section - Made responsive */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-medium text-gray-900">Sales Overview</h2>
            <button
              onClick={() => setRefreshKey(prev => prev + 1)}
              className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              title="Refresh data"
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
          <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metrics.salesByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis 
                  yAxisId="left"
                  tickFormatter={(value) => `$${value.toLocaleString()}`}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  tickFormatter={(value) => value.toFixed(0)}
                />
                <Tooltip 
                  formatter={(value: any, name: string) => [
                    name === "sales" 
                      ? `$${parseFloat(value).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : value,
                    name === "sales" ? "Sales" : "Orders"
                  ]}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="sales"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  name="sales"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="orders"
                  stroke="#10B981"
                  strokeWidth={2}
                  name="orders"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Refund Tracker */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900">Refund Tracker</h2>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-sm text-gray-500">Total Refunds</p>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{metrics.refundMetrics.total}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <p className="text-sm text-gray-500">Refund Amount</p>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                ${metrics.refundMetrics.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Recent Refunds</h3>
            <div className="space-y-3">
              {metrics.refundMetrics.recentRefunds.map((order: Booking) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                  onClick={() => setSelectedOrder(order)}
                >
                  <div>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">Order #{order.orderNumber}</p>
                    <p className="text-xs sm:text-sm text-gray-500">{order.customerName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900 text-sm sm:text-base">
                      ${order.refund?.amount.toFixed(2)}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">{order.refund?.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Category Distribution and Monthly Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Product Categories</h2>
          <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={metrics.categories}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value, percent }) => 
                    `${name} (${value}) ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {metrics.categories.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number) => [value, 'Orders']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Monthly Orders</h2>
          <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.salesByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [value, 'Orders']}
                />
                <Bar 
                  dataKey="orders" 
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}